// Convert hex palette to OKLCH custom properties so DOM overlays can blend
// with today's shader palette via color-mix(in oklch, ...). OKLCH is the right
// color space here — perceptually uniform, supported in all modern browsers,
// avoids the muddy mid-tones that sRGB color-mix produces.

export function paletteToCssVars(palette: readonly string[], target: HTMLElement): void {
  const a = palette[0] ?? '#ff00aa';
  const b = palette[1] ?? '#00ffe0';
  const c = palette[2] ?? '#1a0036';
  target.style.setProperty('--herm-pal-a', hexToOklch(a));
  target.style.setProperty('--herm-pal-b', hexToOklch(b));
  target.style.setProperty('--herm-pal-c', hexToOklch(c));
}

// Parse #rrggbb to OKLCH using the standard transform path:
// sRGB → linear-light → OKLab → OKLCH. ~30 lines, no dependency.
function hexToOklch(hex: string): string {
  const m = hex.replace('#', '').padEnd(6, '0').slice(0, 6);
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const lin = (v: number): number => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  const lr = lin(r), lg = lin(g), lb = lin(b);

  // Linear sRGB → LMS (OKLab matrices, Björn Ottosson 2020)
  const l_ = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m_ = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s_ = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l = Math.cbrt(l_), mm = Math.cbrt(m_), s = Math.cbrt(s_);
  const L = 0.2104542553 * l + 0.7936177850 * mm - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.4285922050 * mm + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * mm - 0.8086757660 * s;

  const C = Math.sqrt(A * A + B * B);
  let H = (Math.atan2(B, A) * 180) / Math.PI;
  if (H < 0) H += 360;

  // Format: oklch(L% C H) — keep 1-2 decimals; CSS parses any precision
  return `oklch(${(L * 100).toFixed(1)}% ${C.toFixed(3)} ${H.toFixed(1)})`;
}
